import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { MapPinIcon } from '@heroicons/react/24/outline'
import {
  clearLocationError,
  selectLocationError,
  selectUserLocation,
  locationDetectionFailed,
  detectUserLocationWithPlaceName
} from '../../../../redux/slices/locationSlice'
import { getLocationNameFromCoordinates } from '../../../../utils/locationUtils'
import useFilter from '../useFilter'

/**
 * Location display component showing the current location and distance slider
 * Only displays when local pickup is enabled
 */
const LocationDisplay = () => {
  const dispatch = useDispatch()
  const { filters, handleLocalPickupChange } = useFilter()
  const locationError = useSelector(selectLocationError)
  const userLocation = useSelector(selectUserLocation)
  const [isLoadingLocation, setIsLoadingLocation] = useState(false)
  const [locationDisplay, setLocationDisplay] = useState('Current location')
  const [displayError, setDisplayError] = useState('')

  // Track if pickup filter is active
  const isPickupFilterActive = filters.localPickup?.enabled === true
  const distance = filters.localPickup?.distance || 5
  const maxDistance = 10

  // Helper to check if we have valid location coordinates
  function hasValidLocation() {
    return Boolean(
      (filters.location?.latitude && filters.location?.longitude) ||
      (userLocation?.coordinates?.latitude && userLocation?.coordinates?.longitude)
    )
  }

  // Fetch location name from coordinates when filters change or component mounts
  useEffect(() => {
    const fetchLocationName = async () => {
      if (isPickupFilterActive && filters.location?.latitude && filters.location?.longitude) {
        try {
          setIsLoadingLocation(true)
          setDisplayError('')
          
          const locationData = await getLocationNameFromCoordinates(
            filters.location.latitude,
            filters.location.longitude
          )
          
          if (locationData.displayName) {
            setLocationDisplay(locationData.displayName)
          }
        } catch (error) {
          console.error('Error fetching location name:', error)
          setDisplayError(`Failed to get location name: ${error.message || 'Unknown error'}`)
        } finally {
          setIsLoadingLocation(false)
        }
      }
    }
    
    fetchLocationName()
  }, [filters.location, isPickupFilterActive])

  // Don't render anything if pickup isn't active
  if (!isPickupFilterActive) return null

  // Handle distance slider change
  const handleDistanceChange = e => {
    const newDist = parseFloat(e.target.value)
    handleLocalPickupChange(true, newDist)
  }

  const handleRefreshLocation = async () => {
    if (isLoadingLocation) return
    try {
      setIsLoadingLocation(true)
      setDisplayError('')
      dispatch(clearLocationError())
      
      const locationAction = await dispatch(detectUserLocationWithPlaceName())
      
      if (locationAction?.payload) {
        const locationData = locationAction.payload
        if (locationData.displayName) {
          setLocationDisplay(locationData.displayName)
        }
      } else if (locationAction?.error) {
        setDisplayError(`Location detection failed: ${locationAction.error.message || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error getting location:', error)
      setDisplayError(`Error accessing location: ${error.message || 'Unknown error'}`)
      dispatch(locationDetectionFailed(error.message))
    } finally {
      setIsLoadingLocation(false)
    }
  }

  const renderLocationButton = () => (
    <button
      onClick={handleRefreshLocation}
      disabled={isLoadingLocation}
      className='text-brand-teal hover:text-brand-teal-dark transition-colors flex items-center justify-center shrink-0 p-1 rounded-full bg-brand-teal bg-opacity-10'
      aria-label={isLoadingLocation ? 'Loading location...' : 'Refresh location'}
    >
      {isLoadingLocation ? (
        <svg className='animate-spin h-3.5 w-3.5' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
          <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
          <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
        </svg>
      ) : (
        <MapPinIcon className='w-3.5 h-3.5' strokeWidth={2.5} />
      )}
    </button>
  )

  const displayName = locationDisplay || userLocation?.displayName || userLocation?.city || 'Current location'

  if (locationError || displayError || !hasValidLocation() || isLoadingLocation) {
    let msg = 'Loading location...'
    let isPulse = true
    if (locationError || displayError) {
      msg = displayError || locationError || 'Failure accessing location'
      isPulse = false
    } else if (!hasValidLocation()) {
      msg = 'Getting location...'
    }

    return (
      <div className='mt-2 px-1 flex items-center gap-2'>
        {isPulse ? <MapPinIcon className='w-4 h-4 text-brand-teal animate-pulse' /> : renderLocationButton()}
        <span className={`text-xs ${isPulse ? 'text-gray-500' : 'text-red-500'} truncate`}>
          {msg}
        </span>
      </div>
    )
  }

  // Calculate percentage for gradient
  const pct = (distance / maxDistance) * 100

  return (
    <div className='flex items-center gap-2 w-full pr-1' style={{ animation: "fadeIn 0.2s ease" }}>
      {/* Concise Single-Line Text */}
      <div className='flex items-center gap-1.5 text-[12px] font-medium text-gray-700 whitespace-nowrap overflow-hidden'>
        <span className='font-bold text-brand-teal whitespace-nowrap min-w-[32px]'>
          {distance === 0 ? "0" : distance % 1 === 0 ? distance : distance.toFixed(1)} km
        </span>
        <span className='text-gray-500'>near</span>
        <span className='truncate' style={{ maxWidth: '120px' }}>{displayName}</span>
      </div>

      {/* Slider */}
      <div className='relative flex-grow flex items-center h-5'>
        <input
          type='range'
          min='0' max='10' step='0.5'
          value={distance}
          onChange={handleDistanceChange}
          className='w-full appearance-none cursor-pointer outline-none bg-gray-200'
          style={{
            height: '4px',
            borderRadius: '2px',
            background: `linear-gradient(to right, #1ABCB0 ${pct}%, #E5E7EB ${pct}%)`
          }}
        />
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        input[type="range"] {
          -webkit-appearance: none;
          background: transparent;
        }
        input[type="range"]::-webkit-slider-runnable-track {
          height: 4px;
          border-radius: 2px;
        }
        input[type="range"]::-moz-range-track {
          height: 4px;
          border-radius: 2px;
          border: none;
        }
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 14px; height: 14px;
          margin-top: -5px;
          border-radius: 50%;
          background: #1ABCB0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.15);
          cursor: pointer;
          transition: transform 0.15s;
        }
        input[type="range"]::-webkit-slider-thumb:hover {
          transform: scale(1.15);
        }
        input[type="range"]::-moz-range-thumb {
          width: 14px; height: 14px;
          border-radius: 50%;
          background: #1ABCB0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.15);
          cursor: pointer;
          border: none;
          transition: transform 0.15s;
        }
        input[type="range"]::-moz-range-thumb:hover {
          transform: scale(1.15);
        }
        input[type="range"]:focus {
          outline: none;
        }
      `}</style>
    </div>
  )
}

export default LocationDisplay 